import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../../api/client";
import { fetchBlobUrl } from "../../utils/fetchBlobUrl";

interface HistoricalAotd {
  aotdId: string;
  date: string;
  filename: string;
  title?: string;
  createdBy?: string;
  authorQuote?: string;
  prompt?: string;
  emojis?: string[];
  engineVersion?: string;
  isBirthday?: boolean;
  imageModel?: string;
  paletteTag?: string;
  styleSuffix?: string;
  generationTime?: number;
  totalDurationMs?: number;
  views?: { total: number; verified: number };
  authorViewTime?: { offsetFormatted?: string; offsetMs?: number; rank?: number; totalRanked?: number };
  race?: unknown;
  tweet?: { tweetUrl?: string } | null;
  waybackMachineUrl?: string | null;
}

const fmtMs = (ms?: number) =>
  ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;

const Stat: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="aotd-hist-stat">
    <span className="aotd-hist-stat-label">{label}</span>
    <span className="aotd-hist-stat-value">{children}</span>
  </div>
);

const AotdHistoryAdminPage: React.FC = () => {
  const { date } = useParams<{ date: string }>();
  const [data, setData] = useState<HistoricalAotd | null>(null);
  const [img, setImg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      setImg(null);
      try {
        const res = await apiClient.get(`/api/art-of-the-day/admin/historical/${date}`);
        if (!alive) return;
        setData(res.data);
        if (res.data?.filename) {
          fetchBlobUrl(`/api/art-of-the-day/image-small/${res.data.filename}`)
            .then((u) => { if (alive) setImg(u); })
            .catch(() => {});
        }
      } catch (e: any) {
        if (alive) setError(e?.response?.data?.message || "Failed to load this AOTD.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [date]);

  return (
    <div className="aotd-hist-page">
      <style>{css}</style>
      <div className="aotd-hist-top">
        <Link to="/art-of-the-day/gallery" className="aotd-hist-back">← gallery</Link>
        <span className="aotd-hist-eyebrow">admin · historical AOTD · {date}</span>
      </div>

      {loading && <p className="aotd-hist-muted">loading…</p>}
      {error && <p className="aotd-hist-error">{error}</p>}

      {data && (
        <div className="aotd-hist-grid">
          <div className="aotd-hist-imgwrap">
            {img ? <img src={img} alt={data.title || "AOTD"} /> : <div className="aotd-hist-imgph">loading image…</div>}
          </div>

          <div className="aotd-hist-info">
            <h1 className="aotd-hist-title">
              {data.title || "(untitled)"} {data.isBirthday && <span className="aotd-hist-bday">🎂 birthday</span>}
            </h1>
            <div className="aotd-hist-by">
              by <b>{data.createdBy}</b> · {new Date(data.date).toLocaleDateString()}
              {data.emojis && data.emojis.length > 0 && <span className="aotd-hist-emojis"> {data.emojis.join(" ")}</span>}
            </div>
            {data.authorQuote && <blockquote className="aotd-hist-quote">“{data.authorQuote}”</blockquote>}

            <div className="aotd-hist-stats">
              {!!(data.views && ((data.views.total ?? 0) > 0 || (data.views.verified ?? 0) > 0)) && (
                <Stat label="views">
                  <b>{data.views.total ?? 0}</b> total · {data.views.verified ?? 0} verified
                </Stat>
              )}
              <Stat label="generated in"><b>{fmtMs(data.generationTime)}</b></Stat>
              {data.authorViewTime?.offsetFormatted && (
                <Stat label="author got it">
                  <b>{data.authorViewTime.offsetFormatted}</b> after unlock
                  {typeof data.authorViewTime.rank === "number" && (
                    <> · #{data.authorViewTime.rank}{data.authorViewTime.totalRanked ? ` / ${data.authorViewTime.totalRanked}` : ""} fastest</>
                  )}
                </Stat>
              )}
              <Stat label="engine">{data.engineVersion || "—"}</Stat>
              <Stat label="image model">{data.imageModel || "—"}</Stat>
              {data.paletteTag && <Stat label="palette">{data.paletteTag}</Stat>}
              {data.styleSuffix && <Stat label="style">{data.styleSuffix}</Stat>}
              <Stat label="pipeline duration">{fmtMs(data.totalDurationMs)}</Stat>
              {data.tweet?.tweetUrl && (
                <Stat label="tweet"><a href={data.tweet.tweetUrl} target="_blank" rel="noreferrer">view ↗</a></Stat>
              )}
              {data.waybackMachineUrl && (
                <Stat label="wayback"><a href={data.waybackMachineUrl} target="_blank" rel="noreferrer">archived ↗</a></Stat>
              )}
            </div>

            {data.prompt && (
              <details className="aotd-hist-details">
                <summary>prompt</summary>
                <pre>{data.prompt}</pre>
              </details>
            )}
            {data.race != null && (
              <details className="aotd-hist-details">
                <summary>race data</summary>
                <pre>{JSON.stringify(data.race, null, 2)}</pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const css = `
.aotd-hist-page { max-width: 980px; margin: 0 auto; padding: 20px 16px 80px; font-family: 'JetBrains Mono', monospace; color: rgb(var(--ui-ink)); }
.aotd-hist-top { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; flex-wrap: wrap; }
.aotd-hist-back { font-weight: 700; text-decoration: none; color: rgb(var(--ui-ink)); border-bottom: 2px solid rgb(var(--ui-mint)); }
.aotd-hist-eyebrow { font-size: 12px; color: rgb(var(--ui-muted)); letter-spacing: 0.4px; }
.aotd-hist-muted { color: rgb(var(--ui-muted)); }
.aotd-hist-error { color: #c0392b; font-weight: 700; }
.aotd-hist-grid { display: grid; grid-template-columns: minmax(0, 420px) 1fr; gap: 28px; align-items: start; }
@media (max-width: 760px) { .aotd-hist-grid { grid-template-columns: 1fr; } }
.aotd-hist-imgwrap { border: 2px solid rgb(var(--ui-ink)); border-radius: 6px; overflow: hidden; box-shadow: 5px 5px 0 rgba(0,0,0,0.7); background: #eee; aspect-ratio: 1/1; }
.aotd-hist-imgwrap img { display: block; width: 100%; height: 100%; object-fit: cover; }
.aotd-hist-imgph { display: grid; place-items: center; height: 100%; color: rgb(var(--ui-muted)); font-size: 13px; }
.aotd-hist-title { font-size: 1.5rem; font-weight: 800; letter-spacing: -0.4px; margin: 0 0 6px; line-height: 1.15; }
.aotd-hist-bday { font-size: 12px; background: #fce7f3; color: #be1d63; border: 1.5px solid #f6a8cd; border-radius: 999px; padding: 2px 8px; vertical-align: middle; }
.aotd-hist-by { font-size: 13px; color: rgb(var(--ui-muted)); margin-bottom: 12px; }
.aotd-hist-emojis { font-size: 15px; }
.aotd-hist-quote { font-family: 'Crimson Text', Georgia, serif; font-style: italic; font-size: 1.2rem; border-left: 3px solid rgb(var(--ui-ink)); padding-left: 12px; margin: 0 0 16px; }
.aotd-hist-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 16px; }
.aotd-hist-stat { border: 1.5px solid rgb(var(--ui-ink) / 0.25); border-radius: 6px; padding: 8px 10px; display: flex; flex-direction: column; gap: 3px; }
.aotd-hist-stat-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: rgb(var(--ui-muted)); }
.aotd-hist-stat-value { font-size: 13px; }
.aotd-hist-stat-value a { color: rgb(var(--ui-ink)); text-decoration: underline; text-decoration-color: rgb(var(--ui-mint)); }
.aotd-hist-details { margin-top: 10px; border: 1.5px solid rgb(var(--ui-ink) / 0.25); border-radius: 6px; padding: 8px 10px; }
.aotd-hist-details summary { cursor: pointer; font-weight: 700; font-size: 12px; }
.aotd-hist-details pre { white-space: pre-wrap; word-break: break-word; font-size: 12px; margin: 8px 0 0; max-height: 320px; overflow: auto; background: rgb(var(--ui-ink) / 0.04); padding: 10px; border-radius: 4px; }
`;

export default AotdHistoryAdminPage;
